/**-----------------------------------------------------------------------------------------
* Copyright © 2021 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var core_1 = require("@angular/core");
var kendo_angular_l10n_1 = require("@progress/kendo-angular-l10n");
var forms_1 = require("@angular/forms");
var kendo_angular_common_1 = require("@progress/kendo-angular-common");
var kendo_licensing_1 = require("@progress/kendo-licensing");
var package_metadata_1 = require("../package-metadata");
var floating_label_input_adapter_1 = require("./floating-label-input-adapter");
var isFunction = function (x) { return Object.prototype.toString.call(x) === '[object Function]'; };
var ɵ0 = isFunction;
exports.ɵ0 = ɵ0;
/**
 * Represents the [Kendo UI FloatingLabel component for Angular]({% slug overview_floatinglabel %}).
 * Provides floating labels to `input` elements.
 *
 * The FloatingLabel supports both Template and Reactive Forms and
 * [can contain Kendo UI for Angular Input components such as `kendo-combobox` and `kendo-numerictextbox`,
 * or HTML Input elements with the `kendoTextBox` directive applied]({% slug overview_floatinglabel %}#toc-implementing-floating-labels).
 *
 * @example
 * ```ts
 *
 * _@Component({
 *   selector: 'my-app',
 *   template: `
 *     <kendo-floatinglabel text="First name">
 *       <input [(ngModel)]="name" kendoTextBox />
 *     </kendo-floatinglabel>
 *   `
 * })
 * class AppComponent {
 *     public name = 'John';
 * }
 *
 * ```
 */
var FloatingLabelComponent = /** @class */ (function () {
    function FloatingLabelComponent(elementRef, renderer, changeDetectorRef, localization) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.changeDetectorRef = changeDetectorRef;
        this.localization = localization;
        this.hostClasses = true;
        /**
         * Fires after the floating label position is changed.
         */
        this.positionChange = new core_1.EventEmitter();
        /**
         * @hidden
         */
        this.focused = false;
        /**
         * @hidden
         */
        this.empty = true;
        /**
         * @hidden
         */
        this.invalid = false;
        /**
         * @hidden
         */
        this.labelId = "k-" + kendo_angular_common_1.guid();
        this.autoFillStarted = false;
        kendo_licensing_1.validatePackage(package_metadata_1.packageMetadata);
        this.direction = localization.rtl ? 'rtl' : 'ltr';
        this.renderer.removeAttribute(this.elementRef.nativeElement, "id");
    }
    Object.defineProperty(FloatingLabelComponent.prototype, "labelPosition", {
        /**
         * Represents the current floating label position.
         */
        get: function () {
            if (!this.empty) {
                return 'Out';
            }
            return this.focused ? 'Out' : 'In';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FloatingLabelComponent.prototype, "focusedClass", {
        get: function () {
            return this.focused;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FloatingLabelComponent.prototype, "invalidClass", {
        get: function () {
            return this.invalid;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @hidden
     */
    FloatingLabelComponent.prototype.ngAfterContentInit = function () {
        this.validateSetup();
        var control = new floating_label_input_adapter_1.FloatingLabelInputAdapter(this.kendoInput || this.formControl.valueAccessor, this.formControl);
        this.addHandlers(control);
        this.setLabelFor(control);
    };
    FloatingLabelComponent.prototype.ngAfterViewInit = function () {
        if (this.kendoInput) {
            this.setAriaLabelledby(this.kendoInput);
        }
    };
    /**
     * @hidden
     */
    FloatingLabelComponent.prototype.ngOnDestroy = function () {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    };
    /**
     * @hidden
     */
    FloatingLabelComponent.prototype.textFor = function (key) {
        return this.localization.get(key);
    };
    FloatingLabelComponent.prototype.subscribe = function (control, eventName, handler) {
        if (control[eventName] instanceof core_1.EventEmitter) {
            var subscription = control[eventName].subscribe(handler);
            if (!this.subscription) {
                this.subscription = subscription;
            }
            else {
                this.subscription.add(subscription);
            }
        }
    };
    FloatingLabelComponent.prototype.updateState = function () {
        var empty = function (value) {
            // zero is not an empty value (e.g., NumericTextBox)
            if (value === 0 || value === false) {
                return false;
            }
            // empty arrays are an empty value (e.g., MultiSelect)
            if (Array.isArray(value) && !value.length) {
                return true;
            }
            return !value;
        };
        var formControl = this.formControl;
        if (formControl) {
            var valueAccessor = formControl.valueAccessor;
            if (isFunction(valueAccessor.isEmpty)) {
                this.empty = valueAccessor.isEmpty();
            }
            else {
                this.empty = empty(formControl.value);
            }
            this.invalid = formControl.invalid && (formControl.touched || formControl.dirty);
        }
        else {
            this.empty = isFunction(this.kendoInput.isEmpty) ?
                this.kendoInput.isEmpty() : empty(this.kendoInput.value);
        }
        if (this.empty) {
            this.renderer.addClass(this.elementRef.nativeElement, 'k-state-empty');
        }
        else {
            this.renderer.removeClass(this.elementRef.nativeElement, 'k-state-empty');
        }
        this.changeDetectorRef.markForCheck();
    };
    FloatingLabelComponent.prototype.setAriaLabelledby = function (component) {
        var componentId = component.focusableId || component.id;
        if (componentId) {
            var focusableElement = this.elementRef.nativeElement.querySelector("#" + componentId);
            this.renderer.setAttribute(focusableElement, 'aria-labelledby', this.labelId);
        }
    };
    FloatingLabelComponent.prototype.setLabelFor = function (control) {
        var controlId = control.focusableId || control.id;
        if (this.id && controlId) {
            // input wins
            this.id = controlId;
        }
        else if (this.id) {
            control.focusableId = this.id;
        }
        else if (controlId) {
            this.id = controlId;
        }
        else {
            var id = "k-" + kendo_angular_common_1.guid();
            control.focusableId = id;
            this.id = id;
        }
    };
    FloatingLabelComponent.prototype.handleAutofill = function (control) {
        var _this = this;
        this.subscribe(control, 'autoFillStart', function () {
            _this.autoFillStarted = true;
            _this.renderer.removeClass(_this.elementRef.nativeElement, 'k-state-empty');
        });
        this.subscribe(control, 'autoFillEnd', function () {
            if (_this.autoFillStarted) {
                _this.autoFillStarted = false;
                if (_this.empty) {
                    _this.renderer.addClass(_this.elementRef.nativeElement, 'k-state-empty');
                }
            }
        });
    };
    FloatingLabelComponent.prototype.addHandlers = function (control) {
        var _this = this;
        var setFocus = function (isFocused) { return function () {
            _this.focused = isFocused;
            _this.updateState();
            if (!_this.empty) {
                return;
            }
            if (kendo_angular_common_1.hasObservers(_this.positionChange)) {
                _this.positionChange.emit(isFocused ? 'Out' : 'In');
            }
        }; };
        this.subscribe(control, 'onFocus', setFocus(true));
        this.subscribe(control, 'onBlur', setFocus(false));
        this.handleAutofill(control);
        var updateState = function () { return _this.updateState(); };
        updateState();
        this.subscribe(control, 'onValueChange', updateState);
    };
    FloatingLabelComponent.prototype.validateSetup = function () {
        if (!this.formControl && !this.kendoInput) {
            if (core_1.isDevMode()) {
                throw new Error("The FloatingLabelComponent requires a Kendo Input component" +
                    " or a forms-bound component to function properly.");
            }
            return;
        }
    };
    tslib_1.__decorate([
        core_1.HostBinding('class.k-floating-label-container'),
        tslib_1.__metadata("design:type", Boolean)
    ], FloatingLabelComponent.prototype, "hostClasses", void 0);
    tslib_1.__decorate([
        core_1.HostBinding('class.k-state-focused'),
        tslib_1.__metadata("design:type", Boolean),
        tslib_1.__metadata("design:paramtypes", [])
    ], FloatingLabelComponent.prototype, "focusedClass", null);
    tslib_1.__decorate([
        core_1.HostBinding('class.k-state-invalid'),
        tslib_1.__metadata("design:type", Boolean),
        tslib_1.__metadata("design:paramtypes", [])
    ], FloatingLabelComponent.prototype, "invalidClass", null);
    tslib_1.__decorate([
        core_1.HostBinding('attr.dir'),
        tslib_1.__metadata("design:type", String)
    ], FloatingLabelComponent.prototype, "direction", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object)
    ], FloatingLabelComponent.prototype, "labelCssStyle", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object)
    ], FloatingLabelComponent.prototype, "labelCssClass", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], FloatingLabelComponent.prototype, "id", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], FloatingLabelComponent.prototype, "text", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], FloatingLabelComponent.prototype, "optional", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], FloatingLabelComponent.prototype, "positionChange", void 0);
    tslib_1.__decorate([
        core_1.ContentChild(kendo_angular_common_1.KendoInput, { static: false }),
        tslib_1.__metadata("design:type", Object)
    ], FloatingLabelComponent.prototype, "kendoInput", void 0);
    tslib_1.__decorate([
        core_1.ContentChild(forms_1.NgControl, { static: false }),
        tslib_1.__metadata("design:type", forms_1.NgControl)
    ], FloatingLabelComponent.prototype, "formControl", void 0);
    FloatingLabelComponent = tslib_1.__decorate([
        core_1.Component({
            selector: 'kendo-floatinglabel',
            exportAs: 'kendoFloatingLabel',
            providers: [
                kendo_angular_l10n_1.LocalizationService,
                {
                    provide: kendo_angular_l10n_1.L10N_PREFIX,
                    useValue: 'kendo.floatinglabel'
                }
            ],
            template: "\n        <ng-container kendoFloatingLabelLocalizedMessages\n            i18n-optional=\"kendo.floatinglabel.optional|The text for the optional segment of a FloatingLabel component\"\n            optional=\"Optional\"\n         >\n        </ng-container>\n        <ng-content></ng-content>\n        <label *ngIf=\"text\" [ngClass]=\"labelCssClass\" [ngStyle]=\"labelCssStyle\" [for]=\"id\" [attr.id]=\"labelId\" class=\"k-label\">\n            {{ text }}<span *ngIf=\"optional\" class=\"k-label-optional\">({{textFor('optional')}})</span>\n        </label>\n    "
        }),
        tslib_1.__metadata("design:paramtypes", [core_1.ElementRef,
            core_1.Renderer2,
            core_1.ChangeDetectorRef,
            kendo_angular_l10n_1.LocalizationService])
    ], FloatingLabelComponent);
    return FloatingLabelComponent;
}());
exports.FloatingLabelComponent = FloatingLabelComponent;
